/**
 * Route status management utility
 * Handles enable/disable functionality based on route status
 */

// Route status constants
export const ROUTE_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  INACTIVE_DEV: 'inactive-dev',
  INACTIVE_PROD: 'inactive-prod',
  DISABLED: 'disabled'
};

/**
 * Get current environment from environment variables
 * @returns {string} Current environment (DEV, PROD, etc.)
 */
export const getCurrentEnvironment = () => {
  return process.env.REACT_APP_STAGE || 'DEV';
};

/**
 * Check if a route should be displayed based on its status and environment
 * @param {Object} route - Route configuration object
 * @param {string} currentEnv - Current environment (optional)
 * @returns {boolean} - Whether the route should be displayed
 */
export const isRouteEnabled = (route, currentEnv = null) => {
  const environment = currentEnv || getCurrentEnvironment();
  
  // If no status is defined, default to active
  if (!route.status) {
    return true;
  }

  switch (route.status) {
    case ROUTE_STATUS.ACTIVE:
      return true;
    
    case ROUTE_STATUS.INACTIVE:
      return false;
    
    case ROUTE_STATUS.INACTIVE_DEV:
      // Inactive only in development, active in production
      return environment !== 'DEV';
    
    case ROUTE_STATUS.INACTIVE_PROD:
      // Inactive only in production, active in development
      return environment !== 'PROD';
    
    case ROUTE_STATUS.DISABLED:
      return false;
    
    default:
      return true; // Default to active for unknown status
  }
};

/**
 * Check if route should be displayed based on environment configuration
 * @param {Object} route - Route configuration object
 * @param {string} currentEnv - Current environment (optional)
 * @returns {boolean} - Whether the route should be displayed in current environment
 */
export const shouldDisplayInEnvironment = (route, currentEnv = null) => {
  const environment = currentEnv || getCurrentEnvironment();
  
  // If no environment restriction is specified, show the route
  if (!route.environment) {
    return true;
  }

  // Handle comma-separated string format (e.g., "DEV, PROD")
  if (typeof route.environment === 'string') {
    const environments = route.environment
      .split(',')
      .map(env => String(env || '').trim().toUpperCase());
    return environments.includes(environment.toUpperCase());
  }
  
  // Handle array format
  if (Array.isArray(route.environment)) {
    return route.environment
      .map(env => env.toUpperCase())
      .includes(environment.toUpperCase());
  }
  
  // Handle single environment string
  return route.environment.toUpperCase() === environment.toUpperCase();
};

/**
 * Filter routes based on status, environment, and role
 * @param {Array} routes - Array of route objects
 * @param {string} userRole - Current user role
 * @param {string} currentEnv - Current environment (optional)
 * @param {boolean} includeDisabled - Whether to include disabled routes for display (optional)
 * @returns {Array} - Filtered routes array
 */
export const filterRoutes = (routes, userRole, currentEnv = null, includeDisabled = false) => {
  return routes.filter(route => {
    // Check if route has required properties
    if (!route.component && !includeDisabled) {
      return false;
    }

    // Check role-based access
    const hasRoleAccess = !route.role || route.role.includes(userRole);
    
    // Check environment-based access
    const hasEnvironmentAccess = shouldDisplayInEnvironment(route, currentEnv);
    
    // Check status-based access
    const isEnabled = includeDisabled || isRouteEnabled(route, currentEnv);
    
    return hasRoleAccess && hasEnvironmentAccess && isEnabled;
  });
};

/**
 * Get routes that should be shown in sidebar (including disabled ones for visual feedback)
 * @param {Array} routes - Array of route objects
 * @param {string} userRole - Current user role
 * @param {string} currentEnv - Current environment (optional)
 * @returns {Array} - Routes to display in sidebar
 */
export const getSidebarRoutes = (routes, userRole, currentEnv = null) => {
  return routes.filter(route => {
    // Must have icon to appear in sidebar
    if (!route.icon) {
      return false;
    }

    // Check role-based access
    const hasRoleAccess = !route.role || route.role.includes(userRole);
    
    // Check environment-based access
    const hasEnvironmentAccess = shouldDisplayInEnvironment(route, currentEnv);
    
    return hasRoleAccess && hasEnvironmentAccess;
  });
};

/**
 * Get status information for a route
 * @param {Object} route - Route configuration object
 * @param {string} currentEnv - Current environment (optional)
 * @returns {Object} - Status information
 */
export const getRouteStatusInfo = (route, currentEnv = null) => {
  const environment = currentEnv || getCurrentEnvironment();
  const enabled = isRouteEnabled(route, environment);
  
  return {
    enabled,
    status: route.status || ROUTE_STATUS.ACTIVE,
    displayInEnvironment: shouldDisplayInEnvironment(route, environment),
    statusLabel: getStatusLabel(route.status, environment)
  };
};

/**
 * Get human-readable status label
 * @param {string} status - Route status
 * @param {string} environment - Current environment
 * @returns {string} - Status label
 */
export const getStatusLabel = (status, environment) => {
  switch (status) {
    case ROUTE_STATUS.ACTIVE:
      return 'Active';
    case ROUTE_STATUS.INACTIVE:
      return 'Inactive';
    case ROUTE_STATUS.INACTIVE_DEV:
      return environment === 'DEV' ? 'Inactive in Development' : 'Active in Production';
    case ROUTE_STATUS.INACTIVE_PROD:
      return environment === 'PROD' ? 'Inactive in Production' : 'Active in Development';
    case ROUTE_STATUS.DISABLED:
      return 'Disabled';
    default:
      return 'Active';
  }
};